import { Globe, Plus } from 'lucide-react';
import { useState } from 'react';
import { CreateRegionModal } from '../components/CreateRegionModal';
import { DeleteRegionModal } from '../components/DeleteRegionModal';
import { EditRegionModal } from '../components/EditRegionModal';
import { RegionDetailModal } from '../components/RegionDetailModal';
import { RegionsTreeView } from '../components/RegionsTreeView';
import { ToggleRegionStatusModal } from '../components/ToggleRegionStatusModal';
import { useRegion } from '../hooks/useRegion';
import Protected from '@/shared/components/ahkinia/Protected/Protected';
import { SectionHeader } from '@/shared/components/ui/header';
import { UnifiedButton } from '@/shared/components/ui/unified-button';
import { PERMISSIONS } from '@/shared/constants/permissions';

export default function RegionsPage() {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [childParentId, setChildParentId] = useState<string | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailRegionId, setDetailRegionId] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editRegionId, setEditRegionId] = useState<string | null>(null);
  const [toggledRegion, setToggledRegion] = useState<{
    id: string;
    name: string;
    isActive: boolean;
  } | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletedRegion, setDeletedRegion] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const { region: parentRegion } = useRegion(
    createModalOpen ? (childParentId ?? undefined) : undefined,
  );

  const handleCreateClose = () => {
    setCreateModalOpen(false);
    setChildParentId(null);
  };

  const handleAddChild = (regionId: string) => {
    setChildParentId(regionId);
    setCreateModalOpen(true);
  };

  const handleViewDetail = (regionId: string) => {
    setDetailRegionId(regionId);
    setDetailModalOpen(true);
  };

  const handleDetailOpenChange = (open: boolean) => {
    setDetailModalOpen(open);
    if (!open) {
      setDetailRegionId(null);
    }
  };

  const handleEdit = (regionId: string) => {
    console.log({ regionId });
    setEditRegionId(regionId);
    setEditModalOpen(true);
  };

  const handleToggleStatus = (regionId: string, regionName: string, currentIsActive: boolean) => {
    setToggledRegion({ id: regionId, name: regionName, isActive: currentIsActive });
  };

  const handleDelete = (regionId: string, regionName: string) => {
    console.log({ regionId, regionName });

    setDeletedRegion({ id: regionId, name: regionName });
    setDeleteModalOpen(true);
  };

  return (
    <div data-testid="regions-page" className="flex flex-col gap-6 p-6">
      <SectionHeader sectionIcon={<Globe />} title="Regiones" subtitle="Gestión de regiones">
        <Protected permissions={PERMISSIONS.REGIONS_CREATE}>
          <UnifiedButton
            data-testid="create-region-btn"
            onClick={() => setCreateModalOpen(true)}
            icon={<Plus />}
          >
            Registrar región
          </UnifiedButton>
        </Protected>
      </SectionHeader>

      <RegionsTreeView
        onAddChild={handleAddChild}
        onViewDetail={handleViewDetail}
        onEdit={handleEdit}
        onToggleStatus={handleToggleStatus}
        onDelete={handleDelete}
      />

      <CreateRegionModal
        open={createModalOpen}
        onOpenChange={handleCreateClose}
        initialParentId={childParentId}
        parentSelector={parentRegion?.selector}
      />

      <EditRegionModal
        regionId={editRegionId ?? ''}
        open={editModalOpen}
        onOpenChange={() => {
          setEditModalOpen(false);
          setEditRegionId(null);
        }}
      />

      <RegionDetailModal
        regionId={detailRegionId ?? ''}
        open={detailModalOpen}
        onOpenChange={handleDetailOpenChange}
      />

      {toggledRegion && (
        <ToggleRegionStatusModal
          regionId={toggledRegion.id}
          regionName={toggledRegion.name}
          currentIsActive={toggledRegion.isActive}
          onClose={() => setToggledRegion(null)}
        />
      )}

      <DeleteRegionModal
        regionId={deletedRegion?.id ?? ''}
        regionName={deletedRegion?.name ?? ''}
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeletedRegion(null);
        }}
      />
    </div>
  );
}
